import { Metrics, QuarterlyReport, ChartData, ReportStatus, ReportResponse, ReportAnswer, UploadedFile, TemplateResponse } from '../types/reports';
import { PerformanceData } from '../types/dashboard';
import httpClient from './httpClient';

// Mock data
const mockMetrics: Metrics = {
  totalReports: 40,
  submittedReports: 35,
  completionRate: 87.5,
  testingCoverage: 92.3,
  activeClients: 15
};

const mockChartData: ChartData = {
  monthlyData: [
    { name: 'Jan', value: 65 },
    { name: 'Feb', value: 45 },
    { name: 'Mar', value: 58 },
    { name: 'Apr', value: 71 },
    { name: 'May', value: 89 },
    { name: 'Jun', value: 76 }
  ],
  pieData: [
    { name: 'Completed', value: 65, color: '#4CAF50' },
    { name: 'In Progress', value: 25, color: '#FFC107' },
    { name: 'Overdue', value: 10, color: '#F44336' }
  ],
  lineData: [
    { date: '2024-01-01', submissions: 4 },
    { date: '2024-01-02', submissions: 3 },
    { date: '2024-01-03', submissions: 7 },
    { date: '2024-01-04', submissions: 5 },
    { date: '2024-01-05', submissions: 8 },
    { date: '2024-01-06', submissions: 6 }
  ],
  // Skyway specific data
  clientData: [
    { month: 'Jan', clients: 2 },
    { month: 'Feb', clients: 3 },
    { month: 'Mar', clients: 5 },
    { month: 'Apr', clients: 4 },
    { month: 'May', clients: 7 },
    { month: 'Jun', clients: 9 }
  ],
  // DronePort specific data
  testingData: [
    { category: 'Security', value: 85 },
    { category: 'Performance', value: 73 },
    { category: 'Integration', value: 91 },
    { category: 'Load', value: 67 },
    { category: 'UI', value: 89 }
  ],
  securityData: [
    { date: '2024-01-01', coverage: 78 },
    { date: '2024-02-01', coverage: 82 },
    { date: '2024-03-01', coverage: 85 },
    { date: '2024-04-01', coverage: 91 },
    { date: '2024-05-01', coverage: 93 },
    { date: '2024-06-01', coverage: 95 }
  ]
};

// Mock data for the getPerformanceDataByGroup function to simulate API response
const mockPerformanceData: Record<string, PerformanceData> = {
  'Skyway Range': {
    adjacentInfo: {
      tulsaProducerRevenue: {
        percentChange: 12.5,
        previousValue: 577778,
        currentValue: 650000,
      }
    },
    clientAcquisitions: [
      { quarter: 'Q1 2025', acquisitions: 1, engagements: 3, internationalTesting: 0, annualTargetEngagements: 4, annualTargetAcquisitions: 1, annualTargetInternationalTesting: 0 },
      { quarter: 'Q2 2025', acquisitions: 2, engagements: 4, internationalTesting: 0, annualTargetEngagements: 5, annualTargetAcquisitions: 2, annualTargetInternationalTesting: 0 },
      { quarter: 'Q3 2025', acquisitions: 1, engagements: 5, internationalTesting: 0, annualTargetEngagements: 5, annualTargetAcquisitions: 3, annualTargetInternationalTesting: 1 },
      { quarter: 'Q4 2025', acquisitions: 3, engagements: 7, internationalTesting: 1, annualTargetEngagements: 7, annualTargetAcquisitions: 3, annualTargetInternationalTesting: 2 },
      { quarter: 'Q1 2026', acquisitions: 2, engagements: 4, internationalTesting: 1, annualTargetEngagements: 7, annualTargetAcquisitions: 4, annualTargetInternationalTesting: 3 },
      { quarter: 'Q2 2026', acquisitions: 3, engagements: 5, internationalTesting: 2, annualTargetEngagements: 9, annualTargetAcquisitions: 5, annualTargetInternationalTesting: 4 },
      { quarter: 'Q3 2026', acquisitions: 4, engagements: 8, internationalTesting: 2, annualTargetEngagements: 9, annualTargetAcquisitions: 5, annualTargetInternationalTesting: 5 },
      { quarter: 'Q4 2026', acquisitions: 5, engagements: 6, internationalTesting: 3, annualTargetEngagements: 11, annualTargetAcquisitions: 7, annualTargetInternationalTesting: 7 },
      { quarter: 'Q1 2027', acquisitions: 4, engagements: 9, internationalTesting: 2, annualTargetEngagements: 15, annualTargetAcquisitions: 7, annualTargetInternationalTesting: 9 },
    ],
    quarterlyRevenue: [
      { quarter: 'Q1 2024', currentQuarterAcquisitionRevenue: 15000, priorQuartersAcquisitionRevenue: 0, total: 15000 },
      { quarter: 'Q2 2024', currentQuarterAcquisitionRevenue: 22000, priorQuartersAcquisitionRevenue: 15000, total: 37000 },
      { quarter: 'Q3 2024', currentQuarterAcquisitionRevenue: 34000, priorQuartersAcquisitionRevenue: 22000, total: 56000 },
      { quarter: 'Q4 2024', currentQuarterAcquisitionRevenue: 30000, priorQuartersAcquisitionRevenue: 34000, total: 64000 },
      { quarter: 'Q1 2025', currentQuarterAcquisitionRevenue: 25000, priorQuartersAcquisitionRevenue: 30000, total: 55000 },
      { quarter: 'Q2 2025', currentQuarterAcquisitionRevenue: 42000, priorQuartersAcquisitionRevenue: 25000, total: 67000 }
    ],
    tulsaProducerRevenue: [
      { name: 'Tulsa MSA $', value: 650000, dollarAmount: 650000 },
      { name: 'Outside $', value: 350000, dollarAmount: 350000 },
    ],
    tulsaMSAProducerRevenue: [
      { name: 'InnovationLabs', value: 1125000, format: 'currencyShort' },
      { name: 'DroneX', value: 225000, format: 'currencyShort' },
      { name: 'Midwest Air', value: 105000, format: 'currencyShort' },
    ],
    tulsaEmployment: [
      { name: 'InnovationLabs', value: 45, format: 'percentString' },
      { name: 'DroneX', value: 15, format: 'percentString' },
      { name: 'Midwest Air', value: 30, format: 'percentString' },
    ],
    riskAssessment: [
      { quarter: 'Q1 2025', nodes: 5 },
      { quarter: 'Q2 2025', nodes: 7 },
      { quarter: 'Q3 2025', nodes: 10 },
      { quarter: 'Q4 2025', nodes: 12 },
      { quarter: 'Q1 2026', nodes: 15 },
      { quarter: 'Q2 2026', nodes: 18 }
    ],
  },
  'DronePort Network': {
    adjacentInfo: {
      tulsaProducerRevenue: {
        percentChange: 12.5,  // positive number means increase
        previousValue: 577778, // previous quarter value for Tulsa
        currentValue: 650000,  // current quarter value for Tulsa
      }
    },
    clientAcquisitions: [
      { quarter: 'Q1 2025', acquisitions: 1, engagements: 3, internationalTesting: 0, annualTargetEngagements: 4, annualTargetAcquisitions: 0, annualTargetInternationalTesting: 0 },
      { quarter: 'Q2 2025', acquisitions: 2, engagements: 4, internationalTesting: 0, annualTargetEngagements: 4, annualTargetAcquisitions: 0, annualTargetInternationalTesting: 0 },
      { quarter: 'Q3 2025', acquisitions: 3, engagements: 5, internationalTesting: 0, annualTargetEngagements: 4, annualTargetAcquisitions: 0, annualTargetInternationalTesting: 0 },
      { quarter: 'Q4 2025', acquisitions: 0, engagements: 0, internationalTesting: 0, annualTargetEngagements: 4, annualTargetAcquisitions: 0, annualTargetInternationalTesting: 0 },
      { quarter: 'Q1 2026', acquisitions: 0, engagements: 0, internationalTesting: 0, annualTargetEngagements: 10, annualTargetAcquisitions: 0, annualTargetInternationalTesting: 0 },
      { quarter: 'Q2 2026', acquisitions: 0, engagements: 0, internationalTesting: 0, annualTargetEngagements: 10, annualTargetAcquisitions: 0, annualTargetInternationalTesting: 0 },
      { quarter: 'Q3 2026', acquisitions: 0, engagements: 0, internationalTesting: 0, annualTargetEngagements: 10, annualTargetAcquisitions: 0, annualTargetInternationalTesting: 0 },
      { quarter: 'Q4 2026', acquisitions: 0, engagements: 0, internationalTesting: 0, annualTargetEngagements: 10, annualTargetAcquisitions: 0, annualTargetInternationalTesting: 0 },
      { quarter: 'Q1 2027', acquisitions: 0, engagements: 0, internationalTesting: 0, annualTargetEngagements: 10, annualTargetAcquisitions: 5, annualTargetInternationalTesting: 0 },
    ],
    quarterlyRevenue: [
      { quarter: 'Q1 2024', currentQuarterAcquisitionRevenue: 15000, priorQuartersAcquisitionRevenue: 0, total: 15000 },
      { quarter: 'Q2 2024', currentQuarterAcquisitionRevenue: 22000, priorQuartersAcquisitionRevenue: 18000, total: 40000 },
      { quarter: 'Q3 2024', currentQuarterAcquisitionRevenue: 18000, priorQuartersAcquisitionRevenue: 45000, total: 63000 },
      { quarter: 'Q4 2024', currentQuarterAcquisitionRevenue: 30000, priorQuartersAcquisitionRevenue: 67000, total: 97000 },
      { quarter: 'Q1 2025', currentQuarterAcquisitionRevenue: 25000, priorQuartersAcquisitionRevenue: 92000, total: 117000 },
      { quarter: 'Q2 2025', currentQuarterAcquisitionRevenue: 35000, priorQuartersAcquisitionRevenue: 110000, total: 145000 }
    ],
    tulsaProducerRevenue: [
      { name: 'Tulsa MSA $', value: 650000, dollarAmount: 650000 },
      { name: 'Outside $', value: 350000, dollarAmount: 350000 },
    ],
    tulsaMSAProducerRevenue: [
      { name: 'InnovationLabs', value: 1125000, format: 'currencyShort' },
      { name: 'DroneX', value: 225000, format: 'currencyShort' },
      { name: 'Midwest Air', value: 105000, format: 'currencyShort' },
    ],
    tulsaEmployment: [
      { name: 'InnovationLabs', value: 45, format: 'percentString' },
      { name: 'DroneX', value: 15, format: 'percentString' },
      { name: 'Midwest Air', value: 30, format: 'percentString' },
    ],
    riskAssessment: [
      { quarter: 'Q1 2025', nodes: 5 },
      { quarter: 'Q2 2025', nodes: 7 },
      { quarter: 'Q3 2025', nodes: 10 },
      { quarter: 'Q4 2025', nodes: 12 },
      { quarter: 'Q1 2026', nodes: 15 },
      { quarter: 'Q2 2026', nodes: 18 }
    ],
  },
  'TU - SAFE-T': {
    appliedSecurityControls: [
      { quarter: 'Q1 2025', control1Nodes: 2, control2Nodes: 3, control3Nodes: 5, control4Nodes: 3, total: 13 },
      { quarter: 'Q2 2025', control1Nodes: 2, control2Nodes: 4, control3Nodes: 7, control4Nodes: 2, total: 15 },
      { quarter: 'Q3 2025', control1Nodes: 3, control2Nodes: 4, control3Nodes: 7, control4Nodes: 3, total: 17 },
      { quarter: 'Q4 2025', control1Nodes: 4, control2Nodes: 5, control3Nodes: 8, control4Nodes: 4, total: 21 },
      { quarter: 'Q1 2026', control1Nodes: 4, control2Nodes: 4, control3Nodes: 7, control4Nodes: 5, total: 20 },
      { quarter: 'Q2 2026', control1Nodes: 5, control2Nodes: 4, control3Nodes: 8, control4Nodes: 5, total: 22 }
    ],
    riskDistribution: [
      { name: 'Low Risk', value: 72, format: 'percentString', totalCount: 360, color: '#8884d8' },
      { name: 'Medium Risk', value: 24, format: 'percentString', totalCount: 120, color: '#FFC107' },
      { name: 'High Risk', value: 4, format: 'percentString', totalCount: 20, color: '#F44336' },
    ],
    riskThreshold: [
      { value: 28, label: 'High & Medium Risk Controls' } // 28% = 21% + 7% from riskDistribution
    ],
    riskTrendline: [
      { quarter: 'Q1 2024', lowRisk: 335, mediumRisk: 142, highRisk: 81 },
      { quarter: 'Q2 2024', lowRisk: 341, mediumRisk: 143, highRisk: 84 },
      { quarter: 'Q3 2024', lowRisk: 327, mediumRisk: 149, highRisk: 24 },
      { quarter: 'Q4 2024', lowRisk: 336, mediumRisk: 134, highRisk: 27 },
      { quarter: 'Q1 2025', lowRisk: 341, mediumRisk: 137, highRisk: 24 },
      { quarter: 'Q2 2025', lowRisk: 360, mediumRisk: 120, highRisk: 20 }
    ],
    unmitigatedPoams: [
      { quarter: 'Q1 2024', without: 5, undeveloped: 0, developedUnmonitored: 3, monitoredUnshared: 4, shared: 8 },
      { quarter: 'Q2 2024', without: 6, undeveloped: 2, developedUnmonitored: 3, monitoredUnshared: 4, shared: 8 },
      { quarter: 'Q3 2024', without: 3, undeveloped: 3, developedUnmonitored: 3, monitoredUnshared: 4, shared: 8 },
      { quarter: 'Q4 2024', without: 7, undeveloped: 2, developedUnmonitored: 3, monitoredUnshared: 4, shared: 8 },
      { quarter: 'Q1 2025', without: 6, undeveloped: 5, developedUnmonitored: 3, monitoredUnshared: 4, shared: 8 },
      { quarter: 'Q2 2025', without: 8, undeveloped: 4, developedUnmonitored: 3, monitoredUnshared: 4, shared: 8 },
    ],
  },
  'TU - ASWP': {
    tuAwspMinorsDeclared: [
      { quarter: 'Q1 2025', minors: 25 },
      { quarter: 'Q2 2025', minors: 27 },
      { quarter: 'Q3 2025', minors: 35 },
      { quarter: 'Q4 2025', minors: 35 },
      { quarter: 'Q1 2026', minors: 41 },
      { quarter: 'Q2 2026', minors: 44 }
    ],
    tuAwspIndividualsGraduated: [
      { quarter: 'Q1 2025', graduates: 5 },
      { quarter: 'Q2 2025', graduates: 7 },
      { quarter: 'Q3 2025', graduates: 10 },
      { quarter: 'Q4 2025', graduates: 12 },
      { quarter: 'Q1 2026', graduates: 15 },
      { quarter: 'Q2 2026', graduates: 18 }
    ],
    tuAwspHsStudentsEngaged: [
      { quarter: 'Q1 2025', students: 35 },
      { quarter: 'Q2 2025', students: 36 },
      { quarter: 'Q3 2025', students: 47 },
      { quarter: 'Q4 2025', students: 53 },
      { quarter: 'Q1 2026', students: 55 },
      { quarter: 'Q2 2026', students: 58 }
    ],
    tuAwspTeachersTrained: [
      { quarter: 'Q1 2025', teachers: 5 },
      { quarter: 'Q2 2025', teachers: 7 },
      { quarter: 'Q3 2025', teachers: 10 },
      { quarter: 'Q4 2025', teachers: 12 },
      { quarter: 'Q1 2026', teachers: 15 },
      { quarter: 'Q2 2026', teachers: 18 }
    ],
    tuAwspParticipationByRace: [
      { quarter: 'Q1 2025', participants: 5 },
      { quarter: 'Q2 2025', participants: 7 },
      { quarter: 'Q3 2025', participants: 10 },
      { quarter: 'Q4 2025', participants: 12 },
      { quarter: 'Q1 2026', participants: 15 },
      { quarter: 'Q2 2026', participants: 18 }
    ],
    tuAwspRacialCompletion: [
      { quarter: 'Q1 2025', participants: 5 },
      { quarter: 'Q2 2025', participants: 7 },
      { quarter: 'Q3 2025', participants: 10 },
      { quarter: 'Q4 2025', participants: 12 },
      { quarter: 'Q1 2026', participants: 15 },
      { quarter: 'Q2 2026', participants: 18 }
    ],
    tuAwspGenderParticipation: [
      { quarter: 'Q1 2025', participants: 5 },
      { quarter: 'Q2 2025', participants: 7 },
      { quarter: 'Q3 2025', participants: 10 },
      { quarter: 'Q4 2025', participants: 12 },
      { quarter: 'Q1 2026', participants: 15 },
      { quarter: 'Q2 2026', participants: 18 }
    ],
    tuAwspGenderCompletion: [
      { quarter: 'Q1 2025', participants: 5 },
      { quarter: 'Q2 2025', participants: 7 },
      { quarter: 'Q3 2025', participants: 10 },
      { quarter: 'Q4 2025', participants: 12 },
      { quarter: 'Q1 2026', participants: 15 },
      { quarter: 'Q2 2026', participants: 18 }
    ],
  },
  'inTulsa - ASWP': {
    majorReportsCompleted: [
      { quarter: 'Q1 2024', reports: 15, priorReports: 0, total: 15 },
      { quarter: 'Q2 2024', reports: 22, priorReports: 15, total: 37 },
      { quarter: 'Q3 2024', reports: 34, priorReports: 22, total: 56 },
      { quarter: 'Q4 2024', reports: 30, priorReports: 34, total: 64 },
      { quarter: 'Q1 2025', reports: 25, priorReports: 30, total: 55 },
      { quarter: 'Q2 2025', reports: 42, priorReports: 25, total: 67 }
    ],
    adHocReportsCompleted: [
      { quarter: 'Q3 2024', reports: 34, priorReports: 22, total: 56 },
      { quarter: 'Q4 2024', reports: 30, priorReports: 34, total: 64 },
      { quarter: 'Q1 2025', reports: 25, priorReports: 30, total: 55 },
      { quarter: 'Q2 2025', reports: 42, priorReports: 25, total: 67 }
    ],
    presentationSubmissions: [
      { year: '2024', proposalSubmissions: 2 },
      { year: '2025', proposalSubmissions: 3 },
      { year: '2026', proposalSubmissions: 5 },
      { year: '2027', proposalSubmissions: 11 },
      { year: '2028', proposalSubmissions: 15 },
      { year: '2029', proposalSubmissions: 27 }
    ],
    presentations: [
      { quarter: 'Q1 2024', internal: 5, external: 3, conference: 2 },
      { quarter: 'Q2 2024', internal: 6, external: 4, conference: 1 },
      { quarter: 'Q3 2024', internal: 7, external: 4, conference: 3 },
      { quarter: 'Q4 2024', internal: 5, external: 7, conference: 2 },
      { quarter: 'Q1 2025', internal: 10, external: 8, conference: 4 },
      { quarter: 'Q2 2025', internal: 11, external: 9, conference: 5 }
    ],
  },
  'inTulsa - CNTRL': {
    annualReportsCompleted: [
      { label: 'Annual Reports Completed', value: 15, color: '#8884d8', tooltip: 'tooltip', descriptor: 'descriptor' },
    ]
  },
  'Atlas': {
    atlasAwspProgramParticipants: [
      { quarter: 'Q1 2025', participants: 25 },
      { quarter: 'Q2 2025', participants: 27 },
      { quarter: 'Q3 2025', participants: 35 },
      { quarter: 'Q4 2025', participants: 35 },
      { quarter: 'Q1 2026', participants: 41 },
      { quarter: 'Q2 2026', participants: 44 }
    ],
  },
  'Black Tech Street': {
    btsGaceParticipants: [
      { quarter: 'Q1 2024', participants: 32 },
      { quarter: 'Q2 2024', participants: 37 },
      { quarter: 'Q3 2024', participants: 37 },
      { quarter: 'Q4 2024', participants: 47 },
      { quarter: 'Q1 2025', participants: 53 },
      { quarter: 'Q2 2025', participants: 60 }
    ],
    btsGaceParticipationByRace: [
      { quarter: 'Q1 2024', btsGaceRaceA: 25, btsGaceRaceB: 7, total: 32 },
      { quarter: 'Q2 2024', btsGaceRaceA: 26, btsGaceRaceB: 11, total: 37 },
      { quarter: 'Q3 2024', btsGaceRaceA: 24, btsGaceRaceB: 13, total: 37 },
      { quarter: 'Q4 2024', btsGaceRaceA: 27, btsGaceRaceB: 20, total: 47 },
      { quarter: 'Q1 2025', btsGaceRaceA: 28, btsGaceRaceB: 25, total: 53 },
      { quarter: 'Q2 2025', btsGaceRaceA: 30, btsGaceRaceB: 30, total: 60 }
    ],
    btsGaceCommunityEvents: [
      { quarter: 'Q1 2024', events: 3 },
      { quarter: 'Q2 2024', events: 4 },
      { quarter: 'Q3 2024', events: 5 },
      { quarter: 'Q4 2024', events: 2 },
      { quarter: 'Q1 2025', events: 5 },
      { quarter: 'Q2 2025', events: 4 }
    ],
    btsGaceEducationTrainingEvents: [
      { quarter: 'Q1 2024', largeEvents: 3, smallEvents: 7, total: 10 },
      { quarter: 'Q2 2024', largeEvents: 4, smallEvents: 8, total: 12 },
      { quarter: 'Q3 2024', largeEvents: 3, smallEvents: 7, total: 10 },
      { quarter: 'Q4 2024', largeEvents: 6, smallEvents: 9, total: 15 },
      { quarter: 'Q1 2025', largeEvents: 5, smallEvents: 11, total: 16 },
      { quarter: 'Q2 2025', largeEvents: 7, smallEvents: 12, total: 19 }
    ],
    btsGaceParticipantEducation: [
      { quarter: 'Q1 2025', cyberRecognized: 1, aiFluent: 3, digitalExposure: 4, digitalLiteracy: 3 },
      { quarter: 'Q2 2025', cyberRecognized: 2, aiFluent: 4, digitalExposure: 5, digitalLiteracy: 4 },
      { quarter: 'Q3 2025', cyberRecognized: 1, aiFluent: 5, digitalExposure: 5, digitalLiteracy: 5 },
      { quarter: 'Q4 2025', cyberRecognized: 3, aiFluent: 7, digitalExposure: 7, digitalLiteracy: 6 },
      { quarter: 'Q1 2026', cyberRecognized: 2, aiFluent: 4, digitalExposure: 7, digitalLiteracy: 5 },
      { quarter: 'Q2 2026', cyberRecognized: 3, aiFluent: 5, digitalExposure: 9, digitalLiteracy: 7 },
      { quarter: 'Q3 2026', cyberRecognized: 4, aiFluent: 8, digitalExposure: 9, digitalLiteracy: 8 },
      { quarter: 'Q4 2026', cyberRecognized: 5, aiFluent: 6, digitalExposure: 11, digitalLiteracy: 9 },
      { quarter: 'Q1 2027', cyberRecognized: 4, aiFluent: 9, digitalExposure: 15, digitalLiteracy: 10 },
    ],
    btsGaceEngagementStack: [
      { quarter: 'Q1 2024', labResource: 13, startupFundable: 3, startupScalable: 1, productsDeveloped: 2, productsCommercialized: 2, productsScaled: 1 },
      { quarter: 'Q2 2024', labResource: 14, startupFundable: 4, startupScalable: 1, productsDeveloped: 4, productsCommercialized: 3, productsScaled: 2 },
      { quarter: 'Q3 2024', labResource: 13, startupFundable: 5, startupScalable: 1, productsDeveloped: 3, productsCommercialized: 2, productsScaled: 1 },
      { quarter: 'Q4 2024', labResource: 16, startupFundable: 5, startupScalable: 1, productsDeveloped: 7, productsCommercialized: 4, productsScaled: 3 },
      { quarter: 'Q1 2025', labResource: 15, startupFundable: 7, startupScalable: 1, productsDeveloped: 5, productsCommercialized: 3, productsScaled: 2 },
      { quarter: 'Q2 2025', labResource: 17, startupFundable: 4, startupScalable: 1, productsDeveloped: 7, productsCommercialized: 5, productsScaled: 4 }
    ],
    btsGaceGrants: [
      { quarter: 'Q1 2024', grantProposals: 3, grantsAwarded: 1 },
      { quarter: 'Q2 2024', grantProposals: 4, grantsAwarded: 2 },
      { quarter: 'Q3 2024', grantProposals: 3, grantsAwarded: 1 },
      { quarter: 'Q4 2024', grantProposals: 6, grantsAwarded: 3 },
      { quarter: 'Q1 2025', grantProposals: 5, grantsAwarded: 2 },
      { quarter: 'Q2 2025', grantProposals: 7, grantsAwarded: 4 }
    ]
  },
  'default': {
    reportStatus: [
      { name: 'Completed', value: 60 },
      { name: 'In Progress', value: 30 },
      { name: 'Overdue', value: 10 }
    ],
    submissionTrend: [
      { date: '2024-01-01', submissions: 8 },
      { date: '2024-02-01', submissions: 12 },
      { date: '2024-03-01', submissions: 15 },
      { date: '2024-04-01', submissions: 10 },
      { date: '2024-05-01', submissions: 18 },
      { date: '2024-06-01', submissions: 20 }
    ],
  }
};

// Helper function to simulate API delay
const mockDelay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const reportsApi = {
  getMetrics: async (): Promise<Metrics> => {
    // Simulate fast API response (300ms)
    await mockDelay(300);
    return mockMetrics;
  },

  getQuarterlyReports: async (): Promise<QuarterlyReport[]> => {
    try {
      const response = await httpClient.get('/v1/reports/recent');
      return response.data.map((report: any) => ({
        id: report.report_id,
        name: report.report_name,
        dueDate: report.due_date,
        status: report.submission_status.state.toLowerCase() as ReportStatus,
        isInProgress: report.is_in_progress
      }));
    } catch (error) {
      console.error('Error fetching quarterly reports:', error);
      throw error;
    }
  },

  getAllReports: async (): Promise<QuarterlyReport[]> => {
    try {
      const response = await httpClient.get('/v1/reports/');
      return response.data.map((report: any) => ({
        id: report.report_id,
        name: report.report_name,
        dueDate: report.due_date,
        status: report.submission_status.state.toLowerCase() as ReportStatus,
        isInProgress: report.is_in_progress
      }));
    } catch (error) {
      console.error('Error fetching all reports:', error);
      throw error;
    }
  },

  getAvailableYears: async (): Promise<string[]> => {
    try {
      const response = await httpClient.get('/api/v1/reports/years');
      return response.data;
    } catch (error) {
      console.error('Error fetching available years:', error);
      throw error;
    }
  },

  getReport: async (reportId: string): Promise<ReportResponse> => {
    try {
      const response = await httpClient.get(`/v1/reports/${reportId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching report:', error);
      throw error;
    }
  },

  getChartMetrics: async (): Promise<ChartData> => {
    // Simulate variable API response (600-1500ms)
    await mockDelay(600 + Math.random() * 900);
    return mockChartData;
  },

  saveAnswers: async (reportId: string, answers: ReportAnswer[]): Promise<ReportResponse> => {
    try {
      const response = await httpClient.patch(`/v1/reports/${reportId}`, { answers });
      return response.data;
    } catch (error) {
      console.error('Error saving draft:', error);
      throw error;
    }
  },

  updateReportStatus: async (reportId: string, status: ReportStatus): Promise<ReportResponse> => {
    try {
      const response = await httpClient.patch(`/v1/reports/${reportId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating report status:', error);
      throw error;
    }
  },

  // File operations
  getFiles: async (reportId: string): Promise<UploadedFile[]> => {
    const response = await httpClient.get(`/v1/reports/${reportId}/files/`);
    return response.data.files.map((fileData: any) => ({
      id: fileData.file_id,
      name: fileData.filename.toLowerCase(),
      size: fileData.size,
      uploadDate: fileData.uploaded_at,
      url: fileData.gcs_url,
      type: '.' + fileData.filename.split('.').pop()?.toLowerCase(),
      contentType: fileData.content_type
    }));
  },

  uploadFile: async (
    reportId: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<UploadedFile> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await httpClient.post(`/v1/reports/${reportId}/files/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });

    const uploadedFileData = response.data;
    return {
      id: uploadedFileData.file_id,
      name: uploadedFileData.filename || file.name.toLowerCase(),
      size: uploadedFileData.size || file.size,
      uploadDate: uploadedFileData.uploaded_at || new Date().toISOString(),
      url: uploadedFileData.gcs_url || '#',
      type: '.' + file.name.split('.').pop()?.toLowerCase(),
      contentType: uploadedFileData.content_type
    };
  },

  deleteFile: async (reportId: string, fileId: string): Promise<void> => {
    await httpClient.delete(`/v1/reports/${reportId}/files/${fileId}`);
  },

  getFileDownloadUrl: async (reportId: string, fileId: string): Promise<{ download_url: string }> => {
    const response = await httpClient.get(`/v1/reports/${reportId}/files/${fileId}`);
    return response.data;
  },

  // Template operations
  getTemplateFiles: async (reportId: string): Promise<TemplateResponse> => {
    const response = await httpClient.get(`/v1/reports/${reportId}/files/template`);
    return response.data;
  },

  downloadTemplateFile: async (reportId: string, filename: string): Promise<Blob> => {
    const response = await httpClient.get(`/v1/reports/${reportId}/files/template/${filename}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // New function to get performance data by group
  getPerformanceDataByGroup: async (groupId: string): Promise<PerformanceData> => {
    try {
      // For now, return mock data based on the group ID
      await mockDelay(600 + Math.random() * 900);
      return mockPerformanceData[groupId] || mockPerformanceData['default'];
    } catch (error) {
      console.error('Error fetching performance data:', error);
      throw error;
    }
  }
};